import { useState } from "react";
import { CheckCircle2, Upload } from "lucide-react";
import { OnboardingShell } from "./OnboardingShell";
import {
  parseWorkbook,
  patientImportApi,
  type ImportResult,
  type ImportRow,
} from "../../shared/api/patientImport";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function OnboardingImportPage() {
  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  async function onFile(file: File) {
    setResult(null);
    setError(null);
    setParseError(null);
    const parsed = await parseWorkbook(file);
    if (!parsed || parsed.length === 0) {
      setRows(null);
      setParseError(
        'Không đọc được file. Cần dòng tiêu đề có cột "Họ & Tên" (và tuỳ chọn Email, SĐT, Ngày sinh, Địa chỉ…).'
      );
      return;
    }
    setFileName(file.name);
    setRows(parsed);
  }

  async function doImport() {
    if (!rows) return;
    setImporting(true);
    setError(null);
    try {
      setResult(await patientImportApi.import(rows));
    } catch (e) {
      setError(apiError(e).detail);
    } finally {
      setImporting(false);
    }
  }

  return (
    <OnboardingShell
      step={5}
      title="Bệnh nhân"
      description="Nhập danh sách bệnh nhân sẵn có từ CSV, hoặc thêm thủ công."
      ctaTo="/patients/new"
      ctaLabel="Thêm bệnh nhân thủ công"
      backTo="/onboarding/schedule"
      nextTo="/onboarding/done"
    >
      <div className="grid gap-3">
        <p className="m-0 text-sm text-muted-foreground">
          Tải lên file CSV có dòng tiêu đề với các cột:{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs font-medium text-foreground">
            Họ tên
          </code>
          ,{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs font-medium text-foreground">
            Giới tính
          </code>{" "}
          (Nam/Nữ),{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs font-medium text-foreground">
            Ngày sinh
          </code>{" "}
          (yyyy-mm-dd hoặc dd/MM/yyyy),{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs font-medium text-foreground">
            SĐT
          </code>
          . Cột Họ tên là bắt buộc.
        </p>

        <Input
          type="file"
          className="cursor-pointer file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-muted file:px-3 file:py-1 file:text-sm file:font-medium file:text-foreground"
          accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
        />

        {parseError && <p className="text-sm text-destructive">{parseError}</p>}

        {rows && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm">
              <strong className="font-semibold">{fileName}</strong> — đọc được{" "}
              <strong className="font-semibold">{rows.length}</strong> dòng.
            </span>
            <Button type="button" disabled={importing} onClick={doImport}>
              <Upload aria-hidden="true" />
              {importing ? "Đang nhập…" : `Nhập ${rows.length} bệnh nhân`}
            </Button>
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        {result && (
          <div className="grid gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
              <span>
                Đã nhập <strong className="font-semibold">{result.created}</strong>/
                {result.total} bệnh nhân
                {result.errors.length > 0 ? `, ${result.errors.length} dòng lỗi.` : "."}
              </span>
            </span>
            {result.errors.length > 0 && (
              <ul className="m-0 list-disc pl-[18px] text-[13px]">
                {result.errors.slice(0, 8).map((e) => (
                  <li key={e.index}>
                    Dòng {e.index + 2}
                    {e.fullName ? ` (${e.fullName})` : ""}: {e.error}
                  </li>
                ))}
                {result.errors.length > 8 && <li>…và {result.errors.length - 8} dòng khác</li>}
              </ul>
            )}
          </div>
        )}
      </div>
    </OnboardingShell>
  );
}
